import Cookies from "js-cookie";
import { isMobile } from "react-device-detect";
import { isStandaloneDisplayMode } from "@/utils/browser-environment";

/**
 * 인증 토큰 쿠키의 쓰기 정책을 전담한다.
 *
 * 의존성을 js-cookie / react-device-detect 둘로 제한한 것은 의도적이다.
 * (browser-environment는 react-device-detect 하나만 쓰는 순수 모듈이라 예산 밖이 아니다.)
 * jwt.ts는 @/features/types를 거쳐 node:v8까지 끌고 오는데,
 * axios-client.ts(소비 모듈 40여 개)가 그 그래프를 새로 들이지 않게 하기 위함이다.
 */

export const ACCESS_TOKEN_KEY = "access_token";
export const REFRESH_TOKEN_KEY = "refresh_token";

/** 설치된 PWA에서의 로그인 유지 기간(일). iOS는 ITP가 7일로 깎는다. */
const PERSISTENT_LOGIN_DAYS = 30;

/** 일반 브라우저에서는 standalone 경로를 밟아볼 수 없어서 두는 개발/QA용 스위치. */
const FORCE_PERSISTENT_LOGIN_KEY = "__force_persistent_login";

function isForcedByDeveloper() {
  if (process.env.NODE_ENV === "production" || typeof window === "undefined") {
    return false;
  }

  try {
    return localStorage.getItem(FORCE_PERSISTENT_LOGIN_KEY) === "1";
  } catch (e) {
    // 사파리 프라이빗 모드 등에서 localStorage 접근 자체가 throw할 수 있다.
    return false;
  }
}

/**
 * 홈 화면에 설치된 모바일 PWA에서만 true.
 *
 * isMobile을 AND하는 이유는 데스크탑 크롬/엣지도 이 manifest로 설치가 되기 때문이다.
 * 그대로 두면 공용 PC에 설치한 사용자가 30일 토큰을 갖게 된다.
 * 이 값은 호출할 때마다 평가한다 — display-mode는 런타임에 바뀔 수 있다.
 */
export function isPersistentLoginEnabled() {
  return (isMobile && isStandaloneDisplayMode()) || isForcedByDeveloper();
}

/**
 * domain과 path는 절대 넣지 않는다. js-cookie 3의 기본값 path=/ 를 그대로 써야
 * 옵션 없이 호출되는 기존 Cookies.remove들이 계속 매칭된다.
 */
function tokenCookieAttributes(): Cookies.CookieAttributes {
  return isPersistentLoginEnabled() ? { expires: PERSISTENT_LOGIN_DAYS } : {};
}

export function getAccessToken() {
  return Cookies.get(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return Cookies.get(REFRESH_TOKEN_KEY);
}

/**
 * 있는 값만 기록한다. 둘 중 하나가 없다고 나머지까지 버리면 안 된다 —
 * POST /auth/register 응답에는 refreshToken이 아예 없고(use-sign-up.ts의 SignUpResponse),
 * /auth/refresh도 access token만 돌려줄 수 있다.
 *
 * falsy를 거르는 건 js-cookie가 undefined를 문자열 "undefined"로 기록해버리기 때문이다.
 * 그렇게 오염된 refresh_token은 백엔드에서 404 NOT_FOUND가 된다.
 */
export function setTokens(
  accessToken: string | undefined | null,
  refreshToken: string | undefined | null
) {
  const attributes = tokenCookieAttributes();
  if (accessToken) {
    Cookies.set(ACCESS_TOKEN_KEY, accessToken, attributes);
  }
  if (refreshToken) {
    Cookies.set(REFRESH_TOKEN_KEY, refreshToken, attributes);
  }
}

export function clearTokens() {
  Cookies.remove(ACCESS_TOKEN_KEY);
  Cookies.remove(REFRESH_TOKEN_KEY);
}

/**
 * 기존 토큰을 같은 값으로 다시 심어 만료 시각을 연장한다.
 *
 * iOS의 ITP는 스크립트가 심은 쿠키 수명을 7일로 깎는데, 다시 심으면 그 시계가
 * 다시 돈다. 그래서 앱을 열 때마다 호출하면 "마지막 로그인 후 7일"이 아니라
 * "마지막으로 앱을 연 뒤 7일"이 된다.
 *
 * 영속화가 꺼진 컨텍스트에서는 아무것도 하지 않는다. 안드로이드는 설치된 PWA와
 * 크롬이 쿠키 저장소를 공유해서, 여기서 무조건 다시 쓰면 브라우저 탭이 PWA의
 * 영속 쿠키를 세션 쿠키로 강등시켜 버린다.
 */
export function touchTokenCookies() {
  if (!isPersistentLoginEnabled()) {
    return;
  }
  setTokens(getAccessToken(), getRefreshToken());
}
